import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { trackEventWithUTM } from '../../utils/analytics';
import OptimizedImage from '../OptimizedImage';
import './IntentSelector.css';

// ─────────────────────────────────────────────────────────────────────────────
// ██  CONFIGURATION  ██████████████████████████████████████████████████████████
//
//  • To HIDE an intent:  set  enabled: false
//  • To REORDER intents: change the array order
//  • To UPDATE results:  edit the `results` array inside any intent
//  • Max recommended:    6 intents, 3 results each
//
// ─────────────────────────────────────────────────────────────────────────────

interface PujaResult {
  name: string;
  image: string;
  price: string;
  bookingPath: string;
  tag?: string;           // optional badge — e.g. "Most Booked", "Recommended"
}

interface Intent {
  id: string;
  icon: string;
  label: string;
  sublabel: string;
  enabled: boolean;       // ← set false to hide without deleting
  results: PujaResult[];
}

// ─── INTENT → PUJA MAPPING ───────────────────────────────────────────────────
const INTENTS: Intent[] = [
  {
    id: 'health',
    icon: '🙏',
    label: 'Health & Longevity',
    sublabel: 'Recovery, protection & wellbeing',
    enabled: true,
    results: [
      {
        name: 'Maha Mrityunjaya Pujan',
        image: '/images/maha_mritunjay.jpg',
        price: '₹21,500',
        bookingPath: '/booking/shiv-pujan/maha-mrityunjaya-pujan',
        tag: 'Most Powerful',
      },
      {
        name: 'Rudrabhishekh',
        image: '/images/rudrabhishek.jpg',
        price: '₹25,000',
        bookingPath: '/booking/shiv-pujan/rudrabhishekh',
      },
      {
        name: 'Shani Shanti Pujan',
        image: '/images/navgrah/shani.jpg',
        price: '₹21,000',
        bookingPath: '/booking/navagrah-pujan/shani-shanti-pujan',
      },
    ],
  },
  {
    id: 'career',
    icon: '📈',
    label: 'Career & Finance',
    sublabel: 'Obstacles, growth & prosperity',
    enabled: true,
    results: [
      {
        name: 'Shani Shanti Pujan',
        image: '/images/navgrah/shani.jpg',
        price: '₹21,000',
        bookingPath: '/booking/navagrah-pujan/shani-shanti-pujan',
      },
      {
        name: 'Guru Shanti Pujan',
        image: '/images/navgrah/guru.jpg',
        price: '₹21,000',
        bookingPath: '/booking/navagrah-pujan/guru-shanti-pujan',
      },
      {
        name: 'Budh Shanti Pujan',
        image: '/images/navgrah/budh.jpg',
        price: '₹18,000',
        bookingPath: '/booking/navagrah-pujan/budh-shanti-pujan',
      },
    ],
  },
  {
    id: 'marriage',
    icon: '💍',
    label: 'Marriage & Relationships',
    sublabel: 'Delays, compatibility & harmony',
    enabled: true,
    results: [
      {
        name: 'Manglik Dosh Nivaran',
        image: '/images/dosh_nivaran/mangal_dosh.jpg',
        price: '₹35,000',
        bookingPath: '/booking/dosh-nivaran/manglik-dosh-nivaran',
        tag: 'Recommended',
      },
      {
        name: 'Guru Shanti Pujan',
        image: '/images/navgrah/guru.jpg',
        price: '₹21,000',
        bookingPath: '/booking/navagrah-pujan/guru-shanti-pujan',
      },
      {
        name: 'Shani Shanti Pujan',
        image: '/images/navgrah/shani.jpg',
        price: '₹21,000',
        bookingPath: '/booking/navagrah-pujan/shani-shanti-pujan',
      },
    ],
  },
  {
    id: 'peace',
    icon: '🕊️',
    label: 'Peace of Mind',
    sublabel: 'Anxiety, stress & mental clarity',
    enabled: true,
    results: [
      {
        name: 'Rudrabhishekh',
        image: '/images/rudrabhishek.jpg',
        price: '₹25,000',
        bookingPath: '/booking/shiv-pujan/rudrabhishekh',
      },
       {
        name: 'Rahu Shanti Pujan',
        image: '/images/navgrah/rahu.jpg',
        price: '₹21,000',
        bookingPath: '/booking/navagrah-pujan/rahu-shanti-pujan',
      },
      {
        name: 'Shanti Path',
        image: '/images/shanti_samridhi/shanti_path.jpg',
        price: '₹30,000',
        bookingPath: '/booking/shanti-samridhi/shanti-path',
      },
    ],
  },
  {
    id: 'new-beginning',
    icon: '🏠',
    label: 'New Beginnings',
    sublabel: 'New home, baby, venture or life phase',
    enabled: true,
    results: [
      {
        name: 'Grih Pravesh Pujan',
        image: '/images/shanti_samridhi/grih_pravesh.jpg',
        price: '₹35,000',
        bookingPath: '/booking/shanti-samridhi/grih-pravesh-pujan',
        tag: 'Most Popular',
      },
      {
        name: 'Shubh Vivah',
        image: '/images/sanskar/vivah.jpg',
        price: '₹35,000',
        bookingPath: '/booking/vishist-sanskar/shubh-vivah',
      },
      {
        name: 'Naam Karan Sanskar',
        image: '/images/sanskar/naam_karan.jpg',
        price: '₹30,000',
        bookingPath: '/booking/vishist-sanskar/naam-karan-sanskar',
      },
    ],
  },
  {
    id: 'dosha',
    icon: '⚖️',
    label: 'Dosha Nivaran',
    sublabel: 'Kaal Sarp, Manglik, Pitra & more',
    enabled: true,
    results: [
      {
        name: 'Manglik Dosh Nivaran',
        image: '/images/dosh_nivaran/mangal_dosh.jpg',
        price: '₹35,000',
        bookingPath: '/booking/dosh-nivaran/manglik-dosh-nivaran',
        
      },
     {
        name: 'Kaal Sarp Dosh Nivaran',
        image: '/images/dosh_nivaran/kaal_sarp.jpg',
        price: '₹35,000',
        bookingPath: '/booking/dosh-nivaran/kaal-sarp-dosh-nivaran',
        
      },
      {
        name: 'Pitra Dosh Nivaran',
        image: '/images/dosh_nivaran/pitra_dosh.jpg',
        price: '₹45,000',
        bookingPath: '/booking/dosh-nivaran/pitra-dosh-nivaran',
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// ██  END CONFIGURATION  ██████████████████████████████████████████████████████
// ─────────────────────────────────────────────────────────────────────────────

const activeIntents = INTENTS.filter((i) => i.enabled);

const IntentSelector: React.FC = () => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const activeIntent = activeIntents.find((i) => i.id === activeId) ?? null;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEventWithUTM('homepage_section_viewed', 'homepage', 'intent_selector');
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleChipClick = (id: string) => {
    const intent = activeIntents.find((i) => i.id === id);
    const isDeselecting = activeId === id;
    // Toggle off if clicking the same chip again
    setActiveId((prev) => (prev === id ? null : id));
    if (!isDeselecting && intent) {
      trackEventWithUTM('homepage_intent_selected', 'intent_selector', id, {
        intent_label: intent.label,
      });
      // On mobile, scroll the results panel into view so the user
      // can see the cards that just appeared below the chips
      if (window.innerWidth < 900) {
        // Use rAF to wait for the DOM to render the results panel first
        requestAnimationFrame(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  };

  return (
    <section ref={sectionRef} className="intent-selector">
      {/* ── Header ────────────────────────────────────────────── */}
      <p className="intent-selector__eyebrow">Find the Right Pooja for You</p>
      <h2 className="intent-selector__title">Select your purpose - we’ll recommend the right Pooja</h2>
      <p className="intent-selector__subtitle">
            Every ritual has a purpose — let us guide you to the right one.
      </p>

      {/* ── Intent chips ──────────────────────────────────────── */}
      <div className="intent-selector__chips" role="group" aria-label="Select your intent">
        {activeIntents.map((intent) => (
          <button
            key={intent.id}
            className={`intent-chip${activeId === intent.id ? ' active' : ''}`}
            onClick={() => handleChipClick(intent.id)}
            aria-pressed={activeId === intent.id}
          >
            <span className="intent-chip__icon" aria-hidden="true">
              {intent.icon}
            </span>
            <span className="intent-chip__text">
              <span className="intent-chip__label">{intent.label}</span>
              <span className="intent-chip__sublabel">{intent.sublabel}</span>
            </span>
          </button>
        ))}
      </div>

      {/* ── Results panel (only shown when an intent is selected) ── */}
      {activeIntent && (
        <div className="intent-results" key={activeIntent.id} ref={resultsRef}>
          <p className="intent-results__heading">
            Recommended for "{activeIntent.label}"
          </p>
          <div className="intent-results__grid">
            {activeIntent.results.map((puja) => (
              <div className="intent-result-card" key={puja.bookingPath}>
                <div className="intent-result-card__image-wrap">
                  <OptimizedImage
                    src={puja.image}
                    alt={puja.name}
                    className="intent-result-card__image"
                  />
                  {puja.tag && (
                    <span className="intent-result-card__tag">{puja.tag}</span>
                  )}
                </div>
                <div className="intent-result-card__body">
                  <h3 className="intent-result-card__name">{puja.name}</h3>
                  <div className="intent-result-card__footer">
                    <div className="intent-result-card__price-wrap">
                      <span className="intent-result-card__price"> {puja.price}</span>
                      <span className="intent-result-card__price-label">Starting Price</span>
                    </div>
                    <Link
                      to={puja.bookingPath}
                      className="intent-result-card__book-btn"
                      onClick={() =>
                        trackEventWithUTM('homepage_intent_book_now', 'intent_selector', puja.name, {
                          intent_id: activeIntent.id,
                          price: puja.price,
                        })
                      }
                    >
                      Book Now →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="intent-results__fallback">
            Not sure which ritual is right?{' '}
            <Link
              to="/contact"
              onClick={() =>
                trackEventWithUTM('homepage_talk_to_acharya_click', 'intent_selector', activeIntent?.id)
              }
            >
              Talk to an Acharya →
            </Link>
          </p>
        </div>
      )}
    </section>
  );
};

export default IntentSelector;
